import React, { useContext, useMemo, useState, ReactNode } from "react";
import { Box, Card, List, Stack, Typography, capitalize } from "@mui/material";
import { getEntityTypeFromPath, not } from "utils";
import { ListableData, TransferListDataSet } from "types";
import { ToastContext, TOAST_TYPE } from "context/toastContext";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import CollapsableBox from "components/layout/CollapsableBox";

export interface MobileTransferListItemProps<T extends ListableData> {
  item: T;
  included: boolean;
  handleToggle: (value: T) => void;
  expanded?: boolean;
  handleExpand?: (id: number) => void;
  readonly?: boolean;
}

export interface MobileTransferListItemSkeletonProps {
  included?: boolean;
  readonly?: boolean;
}

export interface MobileTransferListProps<T extends ListableData> {
  dataSet: TransferListDataSet<T>;
  setDataSet: (dataSet: TransferListDataSet<T>) => void;
  searchQuery?: string;
  ListItemComponent: React.FC<MobileTransferListItemProps<T>>;
  includedTitle: string;
  excludedTitle: string;
  readonly?: boolean;
  loading?: boolean;
}

const MobileTransferList = <T extends ListableData>({
  dataSet,
  setDataSet,
  searchQuery = "",
  ListItemComponent,
  readonly = false,
  includedTitle,
  excludedTitle,
  loading = false,
}: MobileTransferListProps<T>): ReactNode => {
  const { t } = useTranslation();
  const displayToast = useContext(ToastContext);
  const { pathname } = useLocation();

  const parentEntityName = useMemo(() => {
    return capitalize(getEntityTypeFromPath(pathname));
  }, [pathname]);

  const getItemLabel = (item: T) => {
    if (item?.firstName && item?.lastName) {
      return `${item.firstName} ${item.lastName}`;
    } else if (item?.name) {
      return item.name;
    } else {
      return "";
    }
  };

  const handleIncludeItem = (item: T) => {
    displayToast({
      type: TOAST_TYPE.SUCCESS,
      message: t("toast.itemAdded", {
        name: getItemLabel(item),
        list: parentEntityName,
      }),
    });
    setDataSet({
      excluded: not(dataSet.excluded, [item]),
      included: dataSet.included.concat([item]),
    });
  };
  const handleExcludeItem = (item: T) => {
    displayToast({
      type: TOAST_TYPE.SUCCESS,
      message: t("toast.itemRemoved", {
        name: getItemLabel(item),
        list: parentEntityName,
      }),
    });
    setDataSet({
      excluded: dataSet.excluded.concat([item]),
      included: not(dataSet.included, [item]),
    });
  };

  const [expanded, setExpanded] = useState<number>(0);
  const handleExpandingItemClick = (id: number) => () => {
    if (expanded === id) {
      setExpanded(0);
    } else {
      setExpanded(id);
    }
  };

  const filteredExcluded = useMemo(() => {
    if (!dataSet.excluded) return [];
    if (!searchQuery) return dataSet.excluded;
    const getSafeItemName = (item: T) => {
      if (item?.firstName && item?.lastName) {
        return `${item.firstName} ${item.lastName}`.toLowerCase();
      } else if (item?.name) {
        return item.name.toLowerCase();
      } else {
        return "";
      }
    };
    return dataSet.excluded.filter((item) =>
      getSafeItemName(item).includes(searchQuery.toLowerCase()),
    );
  }, [dataSet.excluded, searchQuery]);

  const filteredIncluded = useMemo(() => {
    if (!dataSet.included) return [];
    if (!searchQuery) return dataSet.included;
    const getSafeItemName = (item: T) => {
      if (item?.firstName && item?.lastName) {
        return `${item.firstName} ${item.lastName}`.toLowerCase();
      } else if (item?.name) {
        return item.name.toLowerCase();
      } else {
        return "";
      }
    };
    return dataSet.included.filter((item) =>
      getSafeItemName(item).includes(searchQuery.toLowerCase()),
    );
  }, [dataSet.included, searchQuery]);

  return (
    <Stack direction="column" display="flex">
      <CollapsableBox
        title={includedTitle}
        startExpanded={false}
        loading={loading}
        expandOnLoad
      >
        <Box sx={{ maxHeight: `calc(100dvh - 620px)`, overflowY: "scroll" }}>
          <List>
            {filteredIncluded.map((item) => (
              <ListItemComponent
                key={item.id}
                item={item}
                included
                handleToggle={handleExcludeItem}
                expanded={expanded === item.id}
                handleExpand={handleExpandingItemClick(item.id)}
                readonly={readonly}
              />
            ))}
            {filteredIncluded.length === 0 && (
              <Card sx={{ px: 2, py: 8 }}>
                <Typography
                  variant={"h6"}
                  align="center"
                  textTransform={"capitalize"}
                >
                  {`${t("noOptions", {
                    name: t("items"),
                  })}`}
                </Typography>
              </Card>
            )}
          </List>
        </Box>
      </CollapsableBox>
      <CollapsableBox
        title={excludedTitle}
        startExpanded={false}
        loading={loading}
      >
        <Box sx={{ maxHeight: `calc(100dvh - 620px)`, overflowY: "scroll" }}>
          <List>
            {filteredExcluded.map((item) => (
              <ListItemComponent
                key={item.id}
                item={item}
                included={false}
                handleToggle={handleIncludeItem}
                expanded={expanded === item.id}
                handleExpand={handleExpandingItemClick(item.id)}
                readonly={readonly}
              />
            ))}
            {filteredExcluded.length === 0 && (
              <Card sx={{ px: 2, py: 8 }}>
                <Typography
                  variant={"h6"}
                  align="center"
                  textTransform={"capitalize"}
                >
                  {`${t("noOptions", {
                    name: t("items"),
                  })}`}
                </Typography>
              </Card>
            )}
          </List>
        </Box>
      </CollapsableBox>
    </Stack>
  );
};

export default MobileTransferList;
